/*
 * Which row is the list's roving stop, published to the rows themselves.
 *
 * A message row carries controls — the actions menu, the reaction chips — and every one of them is
 * a tab stop unless something says otherwise. With three hundred rows in the DOM that is the
 * bypass failure WCAG 2.4.1 is about: the composer would be hundreds of Tab presses below the
 * timeline. The list is therefore one composite widget: the arrow keys choose a row, and only the
 * chosen row's controls are in the tab sequence.
 *
 * It is a subscription and not a context *value* because a value would re-render all three hundred
 * rows on every arrow key. Each row subscribes and re-renders only when its own answer changes, so
 * an arrow press costs two renders.
 *
 * Without a provider — a row drawn on its own, in a test or a preview — every row is active: a
 * single row that nothing is competing with should be fully operable.
 */

import { createContext, useContext, useSyncExternalStore } from "react"
import { Shared } from "../store/shared.ts"

export interface ActiveRowSource {
	subscribe: (listener: () => void) => () => void
	isActive: (seq: number) => boolean
}

const ALWAYS: ActiveRowSource = {
	subscribe: () => () => undefined,
	isActive: () => true,
}

export const ActiveRowContext = createContext<ActiveRowSource>(ALWAYS)

/** True while this row is the one the arrow keys have left the focus on. */
export function useIsActiveRow(seq: number): boolean {
	const source = useContext(ActiveRowContext)
	return useSyncExternalStore(source.subscribe, () => source.isActive(seq))
}

/**
 * The publisher the list owns: one object for its lifetime, so subscriptions survive a render. The
 * stop is one `Shared` seq, and a row's answer is read off it, so a row whose answer did not change
 * re-renders nothing even though every row hears the move.
 */
export function createActiveRowSource(): ActiveRowSource & { set: (seq: number) => void } {
	const active = new Shared(0)
	return {
		subscribe: active.subscribe,
		isActive: (seq) => seq === active.now(),
		set: active.set,
	}
}
